import { useState, useCallback, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { MdAdd, MdDelete } from 'react-icons/md';
import { keysToCamel } from '@blisspointmedia/bpm-types/dist/StyleUtils';
import { useQueryClient } from '@tanstack/react-query';
import { useAppSelector } from '@src/redux';
import { currentCompanySelector } from '@src/redux/company';
import { Page } from '@Components/Page/Page';
import { Button } from '@Components/Button/Button';
import { Intent, Size } from '@Components/designConstants';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { AnomalyDetectionLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import { useSetError } from '@src/redux/modals';
import { emailSelector } from '@src/redux/user';
import { CreateAlertModal } from './CreateAlertModal';
import { EditAlertModal } from './EditAlertModal';
import { AlertsTab } from './AlertsTab';
import { PresetsTab } from './PresetsTab';
import {
  AlertRow,
  GroupedAlert,
  mapGroupedAlertToFormData,
  convertFormDataToDatabaseAlerts,
  sortByLastModifiedDesc
} from './utils/alertUtils';
import { useBudgetOptions } from './hooks/useAlertData';
import './AnomalyDetection.scss';

const TabKey = {
  ALERTS: 'active-alerts',
  PRESETS: 'active-presets'
} as const;
type TabKeyType = (typeof TabKey)[keyof typeof TabKey];

const NAVS: { label: string; key: TabKeyType }[] = [
  { label: 'Active Alerts', key: TabKey.ALERTS },
  { label: 'Active Presets', key: TabKey.PRESETS }
];

const isTabKey = (value: string): value is TabKeyType =>
  (Object.values(TabKey) as string[]).includes(value);

interface AlertsResponse {
  alerts: AlertRow[];
  count: number;
}

export const AnomalyDetection = () => {
  const company = useAppSelector(currentCompanySelector);
  const email = useAppSelector(emailSelector);
  const queryClient = useQueryClient();
  const { data: budgetOptions = [] } = useBudgetOptions(company);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedGroupedAlert, setSelectedGroupedAlert] = useState<GroupedAlert | null>(null);
  const [groupedAlerts, setGroupedAlerts] = useState<GroupedAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState<GroupedAlert | null>(null);
  const setError = useSetError();

  const groupAlerts = useCallback((alerts: AlertRow[]): GroupedAlert[] => {
    const groupMap = new Map<string, AlertRow[]>();

    alerts.forEach(alert => {
      if (!groupMap.has(alert.alertGroupId)) {
        groupMap.set(alert.alertGroupId, [alert]);
      } else {
        groupMap.get(alert.alertGroupId)!.push(alert);
      }
    });

    return Array.from(groupMap.entries()).map(([groupId, groupRows]) => {
      const firstAlert = groupRows[0];
      return {
        alertGroupId: groupId,
        alertName: firstAlert.alertName,
        company: firstAlert.company,
        alertType: firstAlert.alertType,
        checkTypes: groupRows.map(alert => alert.checkType),
        alertChannels: firstAlert.alertChannels,
        startDate: firstAlert.startDate,
        endDate: firstAlert.endDate,
        groupIsActive: firstAlert.groupIsActive,
        groupIsDeleted: firstAlert.groupIsDeleted,
        createdByEmail: firstAlert.createdByEmail,
        dateCreated: firstAlert.dateCreated,
        lastModified: firstAlert.lastModified,
        lastModifiedByEmail: firstAlert.lastModifiedByEmail,
        individualAlerts: groupRows
      };
    });
  }, []);

  const fetchAlerts = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      try {
        // A silent refetch reconciles state in the background (e.g. after an
        // optimistic toggle) without flashing the loading skeleton.
        if (!silent) {
          setLoading(true);
        }
        const response = await AnomalyDetectionLambdaFetch('/alerts', {
          method: 'GET',
          params: { company }
        });
        const data = await awaitJSON<AlertsResponse>(response);
        const camelCaseAlerts = data.alerts.map(alert => keysToCamel(alert)) as AlertRow[];
        const sortedAlerts = sortByLastModifiedDesc(camelCaseAlerts);
        const grouped = groupAlerts(sortedAlerts);
        setGroupedAlerts(grouped);
      } catch (error) {
        setError({
          message: `Failed to fetch alerts: ${
            error instanceof Error ? error.message : String(error)
          }`,
          reportError: error
        });
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [company, setError, groupAlerts]
  );

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'automated-alerting',
    defaultKey: TabKey.ALERTS,
    navs: NAVS.map(nav => nav.key)
  });

  useEffect(() => {
    if (!company) {
      // No resolved company (e.g. the URL slug isn't a company the user can
      // access) — clear the skeleton instead of spinning forever.
      setGroupedAlerts([]);
      setLoading(false);
      return;
    }
    fetchAlerts();
  }, [company, fetchAlerts]);

  const handleToggleActive = async (alertGroupId: string) => {
    const currentGroupedAlert = groupedAlerts.find(group => group.alertGroupId === alertGroupId);
    if (!currentGroupedAlert) {
      return;
    }

    const newActiveState = !currentGroupedAlert.groupIsActive;

    const formData = mapGroupedAlertToFormData(currentGroupedAlert);
    if (!formData) {
      setError({
        message: 'Failed to convert alert data for update',
        reportError: new Error('Form data conversion failed')
      });
      return;
    }

    const alertsForDatabase = convertFormDataToDatabaseAlerts(
      formData,
      company,
      budgetOptions,
      email,
      newActiveState,
      currentGroupedAlert.individualAlerts[0]?.parameters
    );

    // Optimistically flip the toggle, bump "Last Modified" to now, and re-sort so
    // the row jumps to the top immediately — no skeleton, no waiting on the server.
    const priorGroupState = {
      groupIsActive: currentGroupedAlert.groupIsActive,
      lastModified: currentGroupedAlert.lastModified,
      lastModifiedByEmail: currentGroupedAlert.lastModifiedByEmail
    };
    const nowIso = new Date().toISOString();
    setGroupedAlerts(current =>
      sortByLastModifiedDesc(
        current.map(group =>
          group.alertGroupId === alertGroupId
            ? {
                ...group,
                groupIsActive: newActiveState,
                lastModified: nowIso,
                lastModifiedByEmail: email
              }
            : group
        )
      )
    );

    try {
      await AnomalyDetectionLambdaFetch('/alerts/group', {
        method: 'PUT',
        body: {
          alert_group_id: alertGroupId,
          alerts: alertsForDatabase
        }
      });

      // Reconcile with the server's authoritative data without flashing the skeleton.
      await fetchAlerts({ silent: true });
    } catch (error) {
      // Revert only this group (not a whole-array snapshot) so a concurrent
      // toggle on another row isn't clobbered, then surface the failure.
      setGroupedAlerts(current =>
        sortByLastModifiedDesc(
          current.map(group =>
            group.alertGroupId === alertGroupId ? { ...group, ...priorGroupState } : group
          )
        )
      );
      setError({
        message: `Failed to update alert group status: ${
          error instanceof Error ? error.message : String(error)
        }`,
        reportError: error
      });
    }
  };

  const handleEditAlert = (alertGroupId: string) => {
    const groupedAlert = groupedAlerts.find(g => g.alertGroupId === alertGroupId);
    if (groupedAlert) {
      setSelectedGroupedAlert(groupedAlert);
      setShowEditModal(true);
    }
  };

  const handleDeleteAlert = (alertGroupId: string) => {
    const groupedAlert = groupedAlerts.find(g => g.alertGroupId === alertGroupId);
    if (groupedAlert) {
      setAlertToDelete(groupedAlert);
      setShowDeleteConfirmation(true);
    }
  };

  const confirmDeleteAlert = async () => {
    if (!alertToDelete) {
      return;
    }

    try {
      await AnomalyDetectionLambdaFetch('/alerts/group', {
        method: 'DELETE',
        body: {
          alert_group_id: alertToDelete.alertGroupId
        }
      });

      await fetchAlerts();

      setShowDeleteConfirmation(false);
      setAlertToDelete(null);
    } catch (error) {
      setError({
        message: `Failed to delete alert group: ${
          error instanceof Error ? error.message : String(error)
        }`,
        reportError: error
      });
    }
  };

  const cancelDeleteAlert = () => {
    setShowDeleteConfirmation(false);
    setAlertToDelete(null);
  };

  const handlePresetSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['alertPresetsTable', company] });
  };

  const renderAlertsTab = () => (
    <AlertsTab
      loading={loading}
      groupedAlerts={groupedAlerts}
      onToggleActive={handleToggleActive}
      onEditAlert={handleEditAlert}
      onDeleteAlert={handleDeleteAlert}
    />
  );

  const renderPresetsTab = () => <PresetsTab />;

  const renderContent = () => {
    switch (tab) {
      case TabKey.ALERTS:
        return renderAlertsTab();
      case TabKey.PRESETS:
        return renderPresetsTab();
      default:
        return renderAlertsTab();
    }
  };

  return (
    <Page
      title='Automated Alerting'
      pageType='Automated Alerting'
      navs={NAVS}
      selectedNav={tab}
      onNav={nav => {
        if (isTabKey(nav)) {
          goToTab(nav);
        }
      }}
      actions={
        <div className='anomaly-detection-actions'>
          <Button
            size={Size.SM}
            onClick={() => setShowCreateModal(true)}
            className='create-alert-btn'
            leadingElement={<MdAdd className='create-icon' />}
            data-testid='create-alert-button'
          >
            Create Alert
          </Button>
        </div>
      }
    >
      <Container fluid>{renderContent()}</Container>

      <CreateAlertModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSubmit={() => {
          fetchAlerts();
        }}
        onPresetSaved={handlePresetSaved}
      />

      {selectedGroupedAlert && (
        <EditAlertModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setSelectedGroupedAlert(null);
          }}
          groupedAlert={selectedGroupedAlert}
          onSuccess={() => {
            fetchAlerts();
          }}
        />
      )}

      <AlertDialog
        intent={Intent.ERROR}
        show={showDeleteConfirmation}
        onHide={cancelDeleteAlert}
        icon={<MdDelete size={95} />}
        title='Delete Alert?'
        body={`Are you sure you want to delete the alert group "${alertToDelete?.alertName}"? This will delete all ${alertToDelete?.checkTypes.length} alerts in this group. This action cannot be undone.`}
        primaryAction={{ onClick: confirmDeleteAlert, label: 'Delete' }}
        secondaryAction={{ onClick: cancelDeleteAlert }}
      />
    </Page>
  );
};
